import { Loader2, ImageOff, Lock, CheckCircle2 } from "lucide-react";
import Shell from "./Shell";

// Loading state while the gallery is being fetched
export function LoadingState() {
  return (
    <Shell>
      <div className="text-stone-500 flex items-center gap-2 justify-center mt-24">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading gallery…
      </div>
    </Shell>
  );
}

// Error state (404 or other fetch failure)
export function ErrorState({ message }) {
  return (
    <Shell>
      <div className="max-w-md mx-auto mt-24 text-center" data-testid="gallery-error">
        <ImageOff className="w-10 h-10 mx-auto text-stone-400 mb-4" />
        <div className="font-serif text-3xl mb-2">{message}</div>
      </div>
    </Shell>
  );
}

// Password gate shown when the gallery is locked and no password is cached.
export function PasswordGate({ gallery, password, setPassword, onSubmit, authenticating }) {
  return (
    <Shell cover={gallery.cover_image}>
      <div className="max-w-md mx-auto mt-16 text-center" data-testid="gallery-password-gate">
        <div className="eyebrow mb-3">{gallery.studio_name || "Gallery"}</div>
        <h1 className="font-serif text-4xl font-light mb-4">{gallery.name}</h1>
        {gallery.customer_name && <p className="text-stone-500 mb-8">Hi {gallery.customer_name},</p>}
        <Lock className="w-7 h-7 mx-auto text-stone-400 mb-4" />
        <p className="text-sm text-stone-500 mb-6">
          This gallery is password protected. Enter the password from your photographer's email to view it.
        </p>
        <form onSubmit={onSubmit} className="space-y-3" data-testid="gallery-password-form">
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Gallery password"
            className="w-full bg-transparent border-b border-stone-300 py-3 outline-none focus:border-stone-900 text-center"
            data-testid="gallery-password-input"
          />
          <button
            type="submit"
            disabled={!password || authenticating}
            className="w-full px-5 h-11 bg-stone-900 text-stone-50 text-xs uppercase tracking-[0.22em] hover:bg-stone-800 disabled:opacity-50"
            data-testid="gallery-password-submit"
          >
            {authenticating ? "Checking…" : "Enter gallery"}
          </button>
        </form>
      </div>
    </Shell>
  );
}

// Thank-you screen after the client successfully submits their selections.
export function SubmittedView({ gallery, clientName, result }) {
  return (
    <Shell cover={gallery.cover_image}>
      <div className="max-w-xl mx-auto mt-16 text-center" data-testid="gallery-submitted">
        <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600 mb-4" />
        <h1 className="font-serif text-4xl font-light mb-3">Thank you, {clientName}!</h1>
        <p className="text-stone-600">
          Your {result.count} favorite{result.count > 1 ? "s" : ""} have been sent to your photographer.
        </p>
        {result.extra_edit_count > 0 && (
          <div className="mt-6 bg-stone-100 p-5 text-left">
            <div className="eyebrow mb-2">Edit summary</div>
            <div className="text-sm space-y-1">
              <div>Included edits: <strong>{result.included_edits}</strong></div>
              <div>
                Extra edits requested: <strong>{result.extra_edit_count}</strong> ×{" "}
                ${Number(result.extra_edit_price).toFixed(2)}
              </div>
              <div className="pt-2 border-t border-stone-300 font-medium">
                Extras total: ${Number(result.extra_edit_total).toFixed(2)}
              </div>
            </div>
            <p className="text-xs text-stone-500 mt-3">
              Your photographer will follow up to confirm payment for the extras before delivering your final files.
            </p>
          </div>
        )}
        <p className="text-sm text-stone-500 mt-6">
          You can close this page — we'll email you when the high-res files are ready to download.
        </p>
      </div>
    </Shell>
  );
}
